export const SCHEMA_STATUS_LABELS = {
	PENDING: "Queued",
	SCRAPING: "Scraping",
	CLASSIFYING: "Classifying",
	GENERATING: "Generating",
	BATCH_SUBMITTED: "Submitted",
	BATCH_POLLING: "Processing",
	VALIDATING: "Validating",
	SUCCESS: "Done",
	FAILURE: "Failed",
};

export const GENERATED_CONTENT_LABEL = "generated content";

/** Hostname for store/page URL comparison (no www, lowercase). */
export function schemaOptimizerHostname(url) {
	try {
		const raw = String(url || "").trim();
		if (!raw) return "";
		const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
		return new URL(withScheme).hostname.replace(/^www\./, "").toLowerCase();
	} catch {
		return "";
	}
}

/** Whether a page URL belongs to the configured store website (allows subdomains). */
export function urlMatchesStoreDomain(pageUrl, storeWebsiteUrl) {
	const siteHost = schemaOptimizerHostname(storeWebsiteUrl);
	if (!siteHost) return true;

	const pageHost = schemaOptimizerHostname(pageUrl);
	if (!pageHost) return false;

	return pageHost === siteHost || pageHost.endsWith(`.${siteHost}`);
}

export function schemaJobStatusLabel(status) {
	const key = String(status || "").toUpperCase();
	return SCHEMA_STATUS_LABELS[key] || status || "Unknown";
}

/** User-facing progress text — strip vendor / internal wording from API messages. */
export function sanitizeProgressMessage(message) {
	const raw = String(message || "").trim();
	if (!raw) return "";

	if (/generating schema/i.test(raw)) {
		return "Generating schema…";
	}
	if (/batch/i.test(raw) && /generat|poll|submit/i.test(raw)) {
		return "Processing batch…";
	}

	let text = raw
		.replace(/\bOpenAI\b/gi, "")
		.replace(/\(\s*real-time\s*\)/gi, "")
		.replace(/\(\s*batch\s*\)/gi, "")
		.replace(/\s{2,}/g, " ")
		.replace(/\(\s*\)/g, "")
		.trim();
	if (/^generating/i.test(text) && !text.endsWith("…")) {
		text = `${text.replace(/\.+$/, "")}…`;
	}
	return text || raw;
}

export function isGeneratedContentSource(job) {
	const url = job?.url ?? "";
	const sourceType = job?.sourceType ?? job?.source_type;
	return (
		sourceType === "content" ||
		url === GENERATED_CONTENT_LABEL ||
		String(url).startsWith("content:")
	);
}

export function jobSourceLabel(job) {
	const url = job?.url ?? "";
	if (String(url).startsWith("content:")) return url;
	return isGeneratedContentSource(job) ? GENERATED_CONTENT_LABEL : url;
}

export function jobDisplayHostname(job, result) {
	const url = result?.url ?? job?.url ?? job?.domainUrl ?? job?.domain_url ?? "";
	if (isGeneratedContentSource(job)) {
		const domain = job?.domainUrl ?? job?.domain_url ?? "";
		try {
			return new URL(domain).hostname.replace(/^www\./, "");
		} catch {
			return GENERATED_CONTENT_LABEL;
		}
	}
	try {
		return new URL(url).hostname.replace(/^www\./, "");
	} catch {
		return jobSourceLabel(job);
	}
}

export function shorthandUrl(url, maxLen = 52) {
	const text = String(url || "").trim();
	if (!text || text.length <= maxLen) return text;
	const budget = maxLen - 1;
	const head = Math.ceil(budget * 0.55);
	const tail = Math.floor(budget * 0.45);
	return `${text.slice(0, head)}…${text.slice(-tail)}`;
}

export function formatConfidence(value) {
	if (value == null || Number.isNaN(Number(value))) return null;
	const n = Number(value);
	if (n <= 1) return `${Math.round(n * 100)}%`;
	return `${Math.round(n)}%`;
}

export function jobTimestamp(job) {
	const iso =
		job?.updatedAtIso ??
		job?.updated_at_iso ??
		job?.createdAtIso ??
		job?.created_at_iso;
	if (iso) {
		const d = new Date(iso);
		if (!Number.isNaN(d.getTime())) return d;
	}
	const unix = job?.updatedAt ?? job?.updated_at ?? job?.createdAt ?? job?.created_at;
	if (unix) {
		const d = new Date(Number(unix) * 1000);
		if (!Number.isNaN(d.getTime())) return d;
	}
	return null;
}

export function formatJson(value) {
	if (value == null) return "";
	try {
		return JSON.stringify(value, null, 2);
	} catch {
		return String(value);
	}
}

export function normalizeResult(result) {
	if (!result || typeof result !== "object") return null;
	return {
		url: result.url,
		detectedDomain: result.detectedDomain ?? result.detected_domain,
		detectedVertical: result.detectedVertical ?? result.detected_vertical,
		domainConfidence: result.domainConfidence ?? result.domain_confidence,
		originalSchema: result.originalSchema ?? result.original_schema,
		recommendedSchema: result.recommendedSchema ?? result.recommended_schema,
		shaclReport: result.shaclReport ?? result.shacl_report,
		notes: result.notes,
		changes: result.changes
			? {
					added: result.changes.added || [],
					fixed: result.changes.fixed || [],
					couldNotInfer:
						result.changes.couldNotInfer ?? result.changes.could_not_infer ?? [],
					changeLog: (
						result.changes.changeLog ??
						result.changes.change_log ??
						[]
					).map((entry) => ({
						action: entry.action,
						nodeId: entry.node_id ?? entry.nodeId,
						nodeType: entry.node_type ?? entry.nodeType,
						property: entry.property,
						before: entry.before,
						reason: entry.reason,
					})),
				}
			: null,
		usage: result.usage,
	};
}
